/**
 * **영토전 시즌**(Todo9, 사용자가 고른 추천안). "지도는 돌아가도 사람은 남는다."
 *
 * - **길이**: `SEASON_TURNS`(60) 차례. 차례는 출정 한 번이나 영토 전투 한 판이다(서버에서는 주간이 된다).
 * - **승패 — 누적 보유 점수**: 차례마다 그때 쥔 우리 땅(수도 빼고)의 값을 더한다. 땅의 값은 수비 깊이(수도 곁일수록 크다).
 *   끝나는 순간의 땅 수로 매기면 마지막에 몰아쳐 뺏는 쪽이 이긴다 — 오래 지킨 쪽이 이기게 쌓는다.
 * - **등급**: 시작할 때 우리 나라가 쥔 땅의 값 × 60(가만히 다 지켰을 때의 점수)과 견준다. 금 130% 이상, 은 100%, 동 70%.
 *   나라마다 시작한 땅이 달라도 같은 눈금이다.
 * - **보상**: 등급마다 군자금과 장비 — 금은 **그 시즌의 전용 장비**(`SEASON_ITEMS`, 여섯이 시즌마다 돈다 — 칠성검·조우관·
 *   사신기·가지창·어린갑·천고), 은은 4티어, 동은 3티어. 처음에는 금이 그 나라의 5티어 전용(칠지도 등)이었다 — 시즌 전용
 *   그림이 생겨 바꿨다(사용자가 청했다). 기록(`SeasonRecord`)은 계정에 남는다.
 * - **되돌리기**: 땅 주인을 처음으로, 수비대 병사·대장과 다친 병사는 병영으로, 유예·소식은 지운다. 병사·장비·레벨·관등과
 *   특산 장부(사람의 장부)는 그대로다.
 *
 * 차례 번호는 되돌리지 않는다(`seasonStart`로 센다) — 주사위의 시드가 차례에서 나오므로, 0으로 돌리면 시즌마다 같은 결말이
 * 되풀이된다.
 */
import { EQUIPMENT, Source } from '../sim/equipment.ts';
import type { EquipDef } from '../sim/equipment.ts';
import { Rng } from '../sim/rng.ts';
import type { Culture } from '../sim/troops.ts';
import type { Army } from './army.ts';
import { recallGarrison } from './garrison.ts';
import { TERRITORIES, garrisonDepth } from './realm.ts';
import type { RealmState } from './realm.ts';
import { addToStash, canWear } from './stash.ts';

export const SEASON_TURNS = 60;

export const Grade = { Gold: 'gold', Silver: 'silver', Bronze: 'bronze', None: 'none' } as const;
export type Grade = (typeof Grade)[keyof typeof Grade];

export const GRADE_NAMES: Record<Grade, string> = { gold: '금', silver: '은', bronze: '동', none: '등급 없음' };

/** 등급의 문턱(시작 땅을 다 지켰을 때의 점수에 대한 백분율). */
export const GRADE_PCT: Record<Exclude<Grade, 'none'>, number> = { gold: 130, silver: 100, bronze: 70 };
/** 등급의 군자금. */
const GRADE_GOLD: Record<Grade, number> = { gold: 1500, silver: 800, bronze: 400, none: 0 };

export interface SeasonRecord {
  season: number;
  score: number;
  /** 등급을 매긴 눈금(시작 땅을 다 지켰을 때의 점수). */
  par: number;
  grade: Grade;
  gold: number;
  /** 받은 장비 id(없으면 null). */
  item: string | null;
}

/** 지금 시즌 번호(1부터). */
export function seasonOf(realm: RealmState): number {
  return realm.season ?? 1;
}

/** 이번 시즌의 지난 차례 수(0–60). */
export function seasonTurn(realm: RealmState): number {
  return realm.turn - (realm.seasonStart ?? 0);
}

/** 한 차례에 쌓이는 점수: 그때 쥔 우리 땅(수도 빼고)의 수비 깊이 합. */
export function holdingValue(realm: RealmState, culture: Culture): number {
  let n = 0;
  for (const t of TERRITORIES) if (!t.capital && realm.owner[t.id] === culture) n += garrisonDepth(t.id);
  return n;
}

/** 등급의 눈금: 우리 나라가 처음 쥔 땅을 시즌 내내 다 지켰을 때의 점수. */
export function seasonPar(culture: Culture): number {
  let n = 0;
  for (const t of TERRITORIES) if (!t.capital && t.culture === culture) n += garrisonDepth(t.id);
  return n * SEASON_TURNS;
}

export function gradeFor(score: number, par: number): Grade {
  const pct = par > 0 ? Math.floor((score * 100) / par) : 0;
  return pct >= GRADE_PCT.gold ? Grade.Gold : pct >= GRADE_PCT.silver ? Grade.Silver : pct >= GRADE_PCT.bronze ? Grade.Bronze : Grade.None;
}

/** 시즌 전용 장비, 시즌 순서(1·7·13… 칠성검, 2·8… 조우관 …). */
export const SEASON_ITEMS = ['w_chilseong', 'h_jougwan', 'b_sasin', 'w_gajichang', 'a_eorin', 'b_cheongo'] as const;

/** 그 시즌의 전용 장비 id. */
export function seasonItem(season: number): string {
  return SEASON_ITEMS[(Math.max(1, season) - 1) % SEASON_ITEMS.length];
}

/**
 * 등급의 장비 후보: 금은 그 시즌의 전용 장비 하나, 은은 4티어, 동은 3티어(깊이 전리품 — 상점·시즌 전용 밖, 그 나라가 입는 것).
 */
export function gradeItems(grade: Grade, culture: Culture, season: number): EquipDef[] {
  if (grade === Grade.None) return [];
  if (grade === Grade.Gold) return EQUIPMENT.filter((e) => e.id === seasonItem(season));
  const tier = grade === Grade.Silver ? 4 : 3;
  return EQUIPMENT.filter((e) => e.tier === tier && e.source === Source.Depth && canWear(culture, e));
}

/**
 * 이번 차례의 점수를 쌓고, 시즌이 끝났으면 매기고 보상하고 지도를 되돌린다. `realmTick`이 장수의 차례 뒤에 부른다.
 * 끝난 시즌의 기록을 돌려준다(끝나지 않았으면 null).
 */
export function seasonStep(army: Army, realm: RealmState): SeasonRecord | null {
  realm.seasonScore = (realm.seasonScore ?? 0) + holdingValue(realm, army.culture);
  if (seasonTurn(realm) < SEASON_TURNS) return null;
  const score = realm.seasonScore;
  const par = seasonPar(army.culture);
  const grade = gradeFor(score, par);
  const list = gradeItems(grade, army.culture, seasonOf(realm));
  const pick = list.length > 0 ? list[new Rng((Math.imul(seasonOf(realm), 0x9e3779b1) ^ Math.imul(army.culture + 1, 0x85ebca6b)) >>> 0).int(list.length)] : null;
  const gold = GRADE_GOLD[grade];
  army.gold += gold;
  if (pick) addToStash(army, pick.id);
  const record: SeasonRecord = { season: seasonOf(realm), score, par, grade, gold, item: pick?.id ?? null };
  (realm.history ??= []).push(record);
  resetMap(army, realm);
  return record;
}

/** 지도를 처음으로(사람과 장부는 남긴다). 다음 시즌을 연다. */
function resetMap(army: Army, realm: RealmState): void {
  for (const t of TERRITORIES) if (realm.garrisons?.[t.id]) recallGarrison(army, realm, t.id);
  if (realm.infirmary) {
    army.soldiers.push(...realm.infirmary.map((x) => x.soldier));
    army.soldiers.sort((a, b) => a.uid - b.uid);
    delete realm.infirmary;
  }
  for (const t of TERRITORIES) realm.owner[t.id] = t.culture;
  delete realm.garrisons;
  delete realm.heldSince;
  delete realm.log;
  realm.season = seasonOf(realm) + 1;
  realm.seasonStart = realm.turn;
  realm.seasonScore = 0;
}
